import React, { useState } from 'react';
import { Plus, Search, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter,
  DialogClose
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { formatCurrency, formatStock } from '../../lib/utils';

export function MaterialDialog({ 
  open, 
  onOpenChange, 
  formData, 
  setFormData, 
  onSubmit, 
  onReset,
  editingMaterial,
  materials = [] // Lista de materiales existentes
}) {
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedExisting, setSelectedExisting] = useState(null);

  const handleNameChange = (value) => {
    setFormData({...formData, name: value});
    
    if (value.length >= 1 && !editingMaterial) {
      const matches = materials.filter(m => 
        m.name.toLowerCase().includes(value.toLowerCase())
      );
      setNameSuggestions(matches);
      setShowSuggestions(matches.length > 0);
    } else if (value.length === 0 && !editingMaterial) {
      setNameSuggestions(materials);
      setShowSuggestions(true);
    } else {
      setNameSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleReset = () => {
    setSelectedExisting(null);
    setNameSuggestions([]);
    setShowSuggestions(false);
    onReset();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      onOpenChange(isOpen);
      if (!isOpen) handleReset();
    }}>
      <DialogTrigger asChild>
        <Button className="bg-[#931A25] hover:bg-[#7A151F]" data-testid="add-material-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nueva Materia Prima
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editingMaterial ? 'Editar Materia Prima' : 'Agregar Materia Prima'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          {/* Tip para evitar duplicados */}
          {!editingMaterial && !selectedExisting && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                <strong>Tip:</strong> Escribe el nombre para verificar si ya existe.
              </p>
            </div>
          )}
          
          {/* Alerta si seleccionó uno existente */}
          {selectedExisting && (
            <div className="bg-red-50 border border-red-300 rounded-lg p-3">
              <p className="text-sm text-red-800 font-medium">
                ⚠️ Esta materia prima ya existe: "{selectedExisting.name}"
              </p>
              <p className="text-xs text-red-600 mt-1">
                Stock actual: {formatStock(selectedExisting.current_stock)} {selectedExisting.unit} | 
                Costo: {formatCurrency(selectedExisting.average_cost, selectedExisting.currency)}
              </p>
              <p className="text-xs text-red-600 mt-1">
                Para agregar stock, usa el botón "Movimiento" en la tabla.
              </p>
              <Button 
                type="button" 
                variant="outline" 
                size="sm" 
                className="mt-2"
                onClick={() => {
                  setSelectedExisting(null);
                  setFormData({...formData, name: ''});
                }}
              >
                Crear una diferente
              </Button>
            </div>
          )}

          <div className="space-y-2 relative">
            <Label htmlFor="name">
              Nombre * {!editingMaterial && <span className="text-xs text-slate-400">(busca para verificar)</span>}
            </Label>
            <div className="relative">
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                onFocus={() => {
                  if (!editingMaterial) {
                    if (formData.name.length === 0) {
                      setNameSuggestions(materials);
                      setShowSuggestions(materials.length > 0);
                    } else {
                      const matches = materials.filter(m => 
                        m.name.toLowerCase().includes(formData.name.toLowerCase())
                      );
                      setNameSuggestions(matches);
                      setShowSuggestions(matches.length > 0);
                    }
                  }
                }}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                placeholder="Buscar o escribir nombre..."
                required
                disabled={!!selectedExisting}
                data-testid="material-name-input"
                className={selectedExisting ? "border-red-300 bg-red-50" : "pr-8"}
              />
              {!editingMaterial && !selectedExisting && (
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              )}
            </div>
            
            {/* Dropdown de sugerencias */}
            {showSuggestions && nameSuggestions.length > 0 && !editingMaterial && !selectedExisting && (
              <div className="absolute z-50 w-full mt-1 bg-white border-2 border-amber-300 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                <div className="p-2 text-xs font-medium text-amber-700 border-b bg-amber-50 sticky top-0">
                  {formData.name ? `${nameSuggestions.length} coincidencia(s) encontrada(s)` : 'Materias primas existentes:'}
                </div>
                {nameSuggestions.map((mat) => (
                  <div
                    key={mat.id}
                    className="px-3 py-2 hover:bg-red-50 cursor-pointer border-b last:border-0 transition-colors"
                    onMouseDown={() => {
                      setSelectedExisting(mat);
                      setFormData({...formData, name: mat.name});
                      setShowSuggestions(false);
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-sm text-slate-800">{mat.name}</p>
                      <span className="text-xs bg-slate-100 px-2 py-0.5 rounded">
                        Stock: {formatStock(mat.current_stock)} {mat.unit}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Costo: {formatCurrency(mat.average_cost, mat.currency)} / {mat.unit}
                    </p>
                  </div>
                ))}
                {formData.name && (
                  <div 
                    className="px-3 py-2 bg-green-50 hover:bg-green-100 cursor-pointer border-t-2 border-green-200 transition-colors"
                    onMouseDown={() => {
                      setShowSuggestions(false);
                      setNameSuggestions([]);
                    }}
                  >
                    <p className="text-sm text-green-700 font-medium flex items-center gap-2">
                      <Plus className="h-4 w-4" />
                      Crear nueva: "{formData.name}"
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="unit">Unidad de Almacén (Base)</Label>
              <Select 
                value={formData.unit} 
                onValueChange={(v) => setFormData({...formData, unit: v})}
              >
                <SelectTrigger data-testid="material-unit-select">
                  <SelectValue placeholder="Seleccionar unidad..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lb">Libras (lb)</SelectItem>
                  <SelectItem value="kg">Kilogramos (kg)</SelectItem>
                  <SelectItem value="qq">Quintales (qq)</SelectItem>
                  <SelectItem value="saco">Sacos</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">El stock se guardará en esta unidad</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="currency">Moneda de Costos</Label>
              <Select 
                value={formData.currency} 
                onValueChange={(v) => setFormData({...formData, currency: v})}
              >
                <SelectTrigger data-testid="material-currency-select">
                  <SelectValue placeholder="Seleccionar moneda..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DOP">Peso Dominicano (RD$)</SelectItem>
                  <SelectItem value="USD">Dólar (US$)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">Los costos se mostrarán en esta moneda</p>
            </div>
          </div>
          
          {formData.unit === 'saco' && (
            <div className="grid grid-cols-2 gap-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="space-y-2">
                <Label htmlFor="sack_weight">Peso por Saco *</Label>
                <Input
                  id="sack_weight"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={formData.sack_weight}
                  onChange={(e) => setFormData({...formData, sack_weight: parseFloat(e.target.value) || 0})}
                  placeholder="Ej: 50"
                  required
                  data-testid="material-sack-weight-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sack_weight_unit">Unidad del Peso</Label>
                <Select 
                  value={formData.sack_weight_unit} 
                  onValueChange={(v) => setFormData({...formData, sack_weight_unit: v})}
                >
                  <SelectTrigger data-testid="material-sack-weight-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lb">Libras (lb)</SelectItem>
                    <SelectItem value="kg">Kilogramos (kg)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="col-span-2 text-xs text-blue-600">
                Cada saco pesa {formData.sack_weight} {formData.sack_weight_unit === 'lb' ? 'libras' : 'kilogramos'}
              </p>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="min_stock">Stock Mínimo ({formData.unit})</Label>
            <Input
              id="min_stock"
              type="number"
              step="0.01"
              value={formData.min_stock}
              onChange={(e) => setFormData({...formData, min_stock: parseFloat(e.target.value) || 0})}
              onFocus={(e) => e.target.select()}
              data-testid="material-min-stock-input"
            />
          </div>
          {!editingMaterial && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="current_stock">Stock Inicial ({formData.unit})</Label>
                <Input
                  id="current_stock"
                  type="number"
                  step="0.01"
                  value={formData.current_stock}
                  onChange={(e) => setFormData({...formData, current_stock: parseFloat(e.target.value) || 0})}
                  onFocus={(e) => e.target.select()}
                  data-testid="material-current-stock-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="average_cost">Costo Inicial ({formData.currency === 'USD' ? 'US$' : 'RD$'})</Label>
                <Input
                  id="average_cost"
                  type="number"
                  step="0.01"
                  value={formData.average_cost}
                  onChange={(e) => setFormData({...formData, average_cost: parseFloat(e.target.value) || 0})}
                  onFocus={(e) => e.target.select()}
                  data-testid="material-cost-input"
                />
              </div>
            </div>
          )}
          {editingMaterial && (
            <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
              <p>Stock actual: <strong>{formatStock(editingMaterial.current_stock)} {editingMaterial.unit}</strong></p>
              <p>Costo promedio: <strong>{formatCurrency(editingMaterial.average_cost, editingMaterial.currency || 'DOP')}</strong></p>
              <p className="text-xs mt-1 text-slate-500">El stock y costo se modifican mediante compras/movimientos</p>
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" onClick={handleReset}>Cancelar</Button>
            </DialogClose>
            <Button 
              type="submit" 
              className="bg-[#931A25] hover:bg-[#7A151F]" 
              data-testid="save-material-btn"
              disabled={!!selectedExisting}
            >
              {editingMaterial ? 'Actualizar' : 'Guardar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
